import { cn } from '@/shared/lib/utils';
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Body 15/M · secondary */
  label: string;
  /** 오류 상태 — 빨간 테두리·배경. 문자열이면 아래 도움말로 보인다 */
  error?: boolean | string;
  /** 입력칸 오른쪽 (비밀번호 보기 버튼 등) */
  trailing?: ReactNode;
}

/**
 * Figma Input(텍스트) — 라벨 + 56px 입력칸(radius 20 · 1.5px line-grey) + 도움말.
 * 오류: red-50 배경 · red-500 테두리 · red-500 도움말(Body 14/R).
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, trailing, className, id, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helperId = `${inputId}-helper`;
    const hasError = Boolean(error);
    const helper = typeof error === 'string' ? error : null;

    return (
      <div className={cn('flex w-full flex-col gap-1', className)}>
        <label
          htmlFor={inputId}
          className="text-body-15 font-medium text-fg-secondary"
        >
          {label}
        </label>
        <div
          className={cn(
            'flex h-14 w-full items-center gap-3 rounded-20 border-[1.5px] px-3',
            hasError
              ? 'border-red-500 bg-red-50'
              : 'border-line-grey bg-surface-white focus-within:border-fg-accent',
          )}
        >
          <input
            ref={ref}
            id={inputId}
            aria-invalid={hasError || undefined}
            aria-describedby={helper ? helperId : undefined}
            className="min-w-0 flex-1 bg-transparent text-body-16 font-medium text-fg-primary outline-none placeholder:font-regular placeholder:text-fg-light"
            {...props}
          />
          {trailing}
        </div>
        {helper && (
          <p id={helperId} className="text-body-14 font-regular text-red-500">
            {helper}
          </p>
        )}
      </div>
    );
  },
);
TextField.displayName = 'TextField';
